import { takeLatest, put, call, select, getContext, all } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import {
  getActiveRecruitersByLoggedInUserId,
  getRecruiterStaticCount,
} from 'app/services/CandidateServices/CandidateServices';
import { trackPromise } from 'react-promise-tracker';
import { recruiterPlacementActions } from './RecruiterPlacements.redux';
import { selectUser } from 'oidc/user.selectors';
import { IPlacementFilter } from '../MyPlacementsDesk/WorkdeskFilter/filterUtils';
import { getRecruiterList, getPipelineCounts, getTileCounts } from '../MyPlacementsDesk/WorkdeskFilter/filterHelper';
import {
  generateFilterSpecs,
  handleFilterLogic,
  hasUIFilter,
  recruiterPlacementFlattenData,
} from './WorkdeskGrid/helper';
import { trackException } from 'app-insights/appInsightsTracking';
import { ExceptionType } from 'app/enums/Common';
import { PlacementService } from 'app/services/PlacementServices/placement-service';
import { httpSuccess } from 'app/services/serviceHelpers';

const TrackWrapper = (fn, ...args) => trackPromise(fn(...args), 'Recruiter-Placements-call');
const TrackRecStaticCounts = (fn, ...args) => trackPromise(fn(...args), 'Recruiter-Placements-static-counts');
const TrackFalseRecruiters = (fn, ...args) => trackPromise(fn(...args), 'Recruiter-Placements-false-recruiters');
const TrackWrapperRecWorkDeskPipline = (fn, ...args) => trackPromise(fn(...args), 'Recruiter-WorkDesk-Pipeline-call');

export function* requestRecruiterPlacements(
  action: PayloadAction<{
    recruiterId: number[];
    filter?: {
      values: IPlacementFilter;
      setValue: (
        name: string,
        value: unknown,
        options?:
          | Partial<{
              shouldValidate: boolean;
              shouldDirty: boolean;
            }>
          | undefined,
      ) => void;
    };
    arbitrationAgreement: boolean;
  }>,
) {
  const placementService: PlacementService = yield getContext('placementService');
  try {
    const pipelineCounts = yield call(
      TrackWrapperRecWorkDeskPipline,
      placementService.getRecruiterWorkDeskPipeline,
      action.payload.recruiterId,
    );
    if (httpSuccess(pipelineCounts?.status)) {
      yield put(recruiterPlacementActions.setCounts(pipelineCounts.data));
    }

    const response = yield call(
      TrackWrapper,
      placementService.getActivePlacementsByRecruiterById,
      action.payload.recruiterId,
    );

    if (httpSuccess(response?.status)) {
      const { data } = response;

      const flattenedData = recruiterPlacementFlattenData(data.placements);
      const filterSpecs = yield call(generateFilterSpecs, data.filterSpecs, action.payload.arbitrationAgreement);

      yield put(recruiterPlacementActions.setFlattenedData(flattenedData));
      yield put(recruiterPlacementActions.setFilterSpecs(filterSpecs));
      yield put(recruiterPlacementActions.setDataLoaded(true));

      if (action.payload.filter && hasUIFilter(action.payload.filter.values)) {
        yield call(handleFilterLogic, action.payload.filter, filterSpecs, flattenedData);
      } else {
        const tilesCounts = yield call(getTileCounts, flattenedData);
        yield put(recruiterPlacementActions.setTileDynamicCounts(tilesCounts));
      }
    }
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIRequestError,
        functionName: 'requestRecruiterPlacements',
        area: 'src/app/components/RecruiterWorkDesk/MyPlacementsDesk/RecruiterPlacements.saga.ts',
      },
    });
  }
}

export function* requestFalseRecruitersByUserId(action: PayloadAction<{ userId: number }>) {
  try {
    const response = yield call(TrackFalseRecruiters, getActiveRecruitersByLoggedInUserId, action.payload.userId, 'R');
    if (httpSuccess(response.status)) {
      const appUser = yield select(selectUser);
      const recruiters = getRecruiterList(response.data ?? [], appUser.userInfo);
      yield put(recruiterPlacementActions.setFalseRecruiters(recruiters));
    }
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIRequestError,
        functionName: 'requestFalseRecruitersByUserId',
        area: 'src/app/components/RecruiterWorkDesk/MyPlacementsDesk/RecruiterPlacements.saga.ts',
      },
    });
  }
}

export function* requestRecStaticCounts(action: PayloadAction<{ userId: number }>) {
  try {
    const response = yield call(TrackRecStaticCounts, getRecruiterStaticCount, action.payload.userId);
    const counts = {
      nonContactedDate30: response.nonContacted30Days,
      nonContactedDate20: response.nonContacted20Days,
      callBackToday: response.callBackDateToday,
      callBackThisWeek: response.callBackDateWeek,
      lastApplicationToday: response.applicationDateToday,
      lastApplicationThisWeek: response.applicationDateWeek,
      availableToStartThisWeek: response.availableToStartWeek,
      availableToStartNextMonth: response.availableToStartNextMonth,
    };
    yield put(recruiterPlacementActions.setTileStaticCounts(counts));
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIRequestError,
        functionName: 'requestRecStaticCounts',
        area: 'src/app/components/RecruiterWorkDesk/MyPlacementsDesk/RecruiterPlacements.saga.ts',
      },
    });
  }
}

export function* recruiterPlacementSaga() {
  yield takeLatest(recruiterPlacementActions.getRecruiterPlacementAction.type, requestRecruiterPlacements);
  yield takeLatest(recruiterPlacementActions.getFalseRecruitersAction.type, requestFalseRecruitersByUserId);
  yield takeLatest(recruiterPlacementActions.getRecStaticCountAction.type, requestRecStaticCounts);
}
